import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';

type Application = {
    applicationId?: string|number,
    loadInitially?: boolean
};

const initialState = {
    data: {},
    error: null,
    isLoading: false,
};

export const useApplication = (options: Application = {}) => {
    const {
        applicationId: applicationIdOption,
        loadInitially = false,
    } = options;

    const [state, setState] = useState(initialState);

    const { i18n: { language } } = useTranslation();

    const { refetch } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getInvestmentApplication',
        payload: { applicationId: applicationIdOption, language },
        enabled: false,
        staleTime: 2 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
    });

    // Callbacks
    const getApplication = useCallback(async ({ applicationId = applicationIdOption } = {}) => {
        if (!applicationId) {
            setState((prev) => ({ ...prev, error: null, isLoading: false }));

            return null;
        }

        setState((prev) => ({ ...prev, error: null, isLoading: true }));

        // useGetCustomService swallows request errors internally (toasts + returns null)
        const { data: response, error } = await refetch();

        setState({
            data: response?.data ?? {}, error: error ?? null, isLoading: false,
        });

        return response?.data ?? (error ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationIdOption, refetch]);

    // Effects
    useEffect(() => {
        if (loadInitially) getApplication();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadInitially, getApplication]);

    return {
        data: state.data,
        error: state.error,
        isLoading: state.isLoading,
        getApplication,
    };
};
